/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useContext } from 'react';
import Link from 'next/link';
import { slide as Menu } from 'react-burger-menu';
import classNames from 'classnames';
import { timeOfDay } from '@chownow/cn-web-utils/date';

import { postToChownowApi } from 'helpers/api';
import { clearSavedAddressId } from 'helpers/localStorage';
import ModalComponents, { AuthModalStates } from 'types/modals';

import useIsBrowser from 'hooks/useIsBrowser';

import {
  BASE_SUPPORT_URL,
  DOWNLOAD_APP_URL,
  FOR_RESTAURANTS_MOBILE_URL,
  RESTAURANT_LOGIN,
} from '@chownow/cn-web-utils/paths';
import {
  logMparticleEvent,
  mParticleCustomEvents,
  mParticleEventTypes,
  handleAppLinkClick,
  logoutMparticleUser,
} from 'helpers/mParticle';

import { ModalContext } from 'context/modal';
import { CustomerContext } from 'context/customer';

import ButtonLink from 'components/designSystem/ButtonLink/ButtonLink';
import OutLink from 'components/shared/OutLink/OutLink';

import CloseButtonWhite from '../SVG/IconCloseSmall';
import AppDownloadLogo from '../SVG/AppDownloadLogo';
import IconHamburger from '../SVG/IconHamburger';
import styles from './SideBarMenu.module.scss';

interface SidebarMenuProps {
  burgerButtonClass?: string;
  className?: string;
}

type MenuState = {
  isOpen: boolean;
};

function SidebarMenu({ className, burgerButtonClass }: SidebarMenuProps) {
  const { user, setUser } = useContext(CustomerContext);
  const { showModal } = useContext(ModalContext);
  const isBrowser = useIsBrowser();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  function closeMenu(): void {
    // Unlock scrolling when side-menu is closed via navigation
    document.body.style.overflow = 'visible';
    setIsMenuOpen(false);
  }

  function handleMenuStateChange(state: MenuState): void {
    // Locks scrolling when side-menu is open
    document.body.style.overflow = state.isOpen ? 'hidden' : 'visible';

    setIsMenuOpen(state.isOpen);
  }

  function toggleAuthModal(authModalState: AuthModalStates) {
    setIsMenuOpen(false);
    showModal({
      modalType: ModalComponents.AUTH,
      modalProps: {
        activeAuthModal: authModalState,
      },
    });
  }

  async function handleLogout(): Promise<void> {
    const { data } = await postToChownowApi('customer/logout', {});
    if (data) {
      setUser(null);
      logoutMparticleUser();
    }
    clearSavedAddressId();
    closeMenu();
  }

  function handleSupportClick(): void {
    logMparticleEvent({
      eventName: mParticleCustomEvents.selectCustomerSupport,
      eventType: mParticleEventTypes.Other,
      attributes: {
        source: 'side-panel',
        user_is_authenticated: !!user,
      },
    });
    // flushes the event queue and forces the event to send to mParticle
    window.mParticle.upload();
  }

  return (
    <div className={className}>
      {isBrowser && (
        <Menu
          burgerButtonClassName={classNames(styles.burgerBtn, burgerButtonClass)}
          customBurgerIcon={
            <span>
              <IconHamburger />
            </span>
          }
          customCrossIcon={
            <div className={styles.closeButtonWrapper}>
              <CloseButtonWhite />
            </div>
          }
          className={classNames(styles.toggleIcon, styles.wrapper)}
          isOpen={isMenuOpen}
          menuClassName={styles.bmMenu}
          overlayClassName={styles.bmOverlay}
          itemListClassName={styles.bmItemList}
          onStateChange={handleMenuStateChange}
          right
        >
          <div className={styles.main}>
            <header className={styles.header}>
              <h4>
                Good {timeOfDay().toLowerCase()}
                {user && (
                  <>
                    <span>,</span>
                    <div>
                      <span title={user.first_name}>{user.first_name}</span>
                    </div>
                  </>
                )}
              </h4>
            </header>
            <ul className={styles.nav}>
              <li className={styles.item}>
                <Link href="/" onClick={closeMenu} className={styles.link}>
                  Home
                </Link>
              </li>
              {!user && (
                <li className={styles.item}>
                  <ButtonLink
                    className={styles.buttonToLink}
                    onClick={() => toggleAuthModal(AuthModalStates.loginSignUp)}
                  >
                    Log In or Sign Up
                  </ButtonLink>
                </li>
              )}
              {user && (
                <>
                  <li className={styles.item}>
                    <a href="/order/account" className={styles.link}>
                      Account Settings
                    </a>
                  </li>
                  <li className={styles.item}>
                    <a href="/order/history" className={styles.link}>
                      Order History
                    </a>
                  </li>
                  <li className={styles.item}>
                    <OutLink
                      size="medium"
                      ariaLabel="Customer Support"
                      to={BASE_SUPPORT_URL}
                      handleClick={handleSupportClick}
                    >
                      Help
                    </OutLink>
                  </li>
                  <li className={styles.item}>
                    <ButtonLink className={styles.buttonToLink} onClick={handleLogout}>
                      Log Out
                    </ButtonLink>
                  </li>
                </>
              )}
              <li className={styles.divider} />
              <li className={styles.item}>
                <OutLink
                  size="medium"
                  ariaLabel="For Restaurants Link"
                  to={FOR_RESTAURANTS_MOBILE_URL}
                >
                  For Restaurants
                </OutLink>
              </li>
              {!user && (
                <li className={styles.item}>
                  <OutLink size="medium" ariaLabel="Login for Restaurants" to={RESTAURANT_LOGIN}>
                    Restaurant Log In
                  </OutLink>
                </li>
              )}
            </ul>
          </div>
          <div className={styles.footer}>
            <div className={styles.appLinkWrapper}>
              <div className={styles.appLogoWrapper}>
                <OutLink
                  to={DOWNLOAD_APP_URL}
                  ariaLabel="Download the ChowNow App"
                  handleClick={() => {
                    handleAppLinkClick({ user, source: 'side-menu' });
                  }}
                >
                  <AppDownloadLogo aria-hidden="true" />
                </OutLink>
              </div>
              <p className={styles.orderCopy}>
                Love ordering local?{' '}
                <a
                  className={styles.orderLink}
                  href={DOWNLOAD_APP_URL}
                  onClick={() => {
                    handleAppLinkClick({ user, source: 'side-menu' });
                  }}
                >
                  Get the app
                </a>
              </p>
            </div>
          </div>
        </Menu>
      )}
    </div>
  );
}

export default SidebarMenu;
